<template>
    <div>
        <section class="tabs-section">
            <NavBtns @navigatetotab="navigateToTab" :combo-ids="comboIds" :tabs="tabs" />
            <FlowNavigation currentStep="Maps" />
        </section>

        <section class="inner-content-section py-9">
        <div class="maps-container container">
            <!-- Page Header -->
            <div class="maps-header flex items-center mb-4">
                <div class="header-icon w-12 h-12 md:w-16 md:h-16 min-h-12 min-w-12 md:min-h-16 md:min-w-16 mr-3 bg-[var(--light-gray)] p-2 rounded-lg flex items-center justify-center">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                        <path d="M12 21C16.4183 21 20 17.4183 20 13C20 8.58172 16.4183 5 12 5C7.58172 5 4 8.58172 4 13C4 17.4183 7.58172 21 12 21Z" fill="currentColor" opacity="0.2"/>
                        <path d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z" fill="currentColor"/>
                        <path d="M12 21C16.4183 21 20 17.4183 20 13C20 8.58172 16.4183 5 12 5C7.58172 5 4 8.58172 4 13C4 17.4183 7.58172 21 12 21Z" stroke="currentColor" stroke-width="2"/>
                        <path d="M12 3V5M21 13H19M5 13H3M18.364 18.364L16.95 16.95" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                </div>
                <div class="header-content">
                    <h1 class="header-title text-lg lg:text-[22px] text-[var(--charcoal)] font-bold">Trip Map</h1>
                    <p class="header-subtitle text-base lg:text-lg text-[var(--tone-gray)]">Visualize your journey across locations</p>
                </div>
            </div>

            <!-- Combo package minimum selection warning -->
            <div v-if="showComboWarning" class="flex items-start gap-3 bg-[var(--light-primary,#fff8ee)] border border-[var(--primary)] rounded-lg px-4 py-3 text-sm text-[var(--charcoal-dark)] mb-4">
                <svg class="shrink-0 mt-0.5" width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" opacity="0.3"/>
                    <path d="M12 8v4m0 4h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                <span>This booking requires at least <strong>2 packages</strong>. Please go back and select at least one more package before proceeding to checkout.</span>
            </div>

            <!-- Map Container -->
            <div class="map-wrapper w-full h-80 md:h-[400px] bg-[var(--light-gray)] rounded-2xl mb-6">
                <GMapMap 
                    :center="center" 
                    ref="mapRef" 
                    :zoom="10" 
                    class="tour-map w-full h-full"
                    :options="mapOptions" 
                    data-testid="tour-map">
                    <GMapMarker 
                        v-for="(marker, index) in markers" 
                        :key="index"
                        :ref="`marker${index}`" 
                        :position="marker.position" 
                        :clickable="true"
                        :icon="marker.icon" 
                        :title="marker.title" 
                        class="w-full h-full"
                        @click="onMarkerClick(marker)"
                        :data-testid="`marker-${index}`" />
                </GMapMap>
            </div>

            <!-- Tour Details -->
            <div class="tour-details-section bg-white rounded-lg border border-[var(--bone)]">
                <div class="section-header flex items-center border-b border-[var(--bone)] p-4">
                    <h2 class="text-lg lg:text-[22px] text-[var(--charcoal)] font-bold">Your Day</h2>
                </div>

                <div class="tour-cards p-4">
                    <div 
                        v-for="item in cartItem" 
                        :key="item.tour_slot_id"
                        class="tour-card sm:flex items-center mb-6 sm:mb-3"
                        :data-testid="`cart-item-${item.tour_slot_id}`">
                        
                        <div class="tour-card-image w-52 min-w-52 h-44 bg-[var(--light-gray)] rounded-2xl sm:mr-4 border border-[var(--bone)] overflow-hidden max-[639px]:mb-2">
                            <img v-if="item.package_image" :src="item.package_image" :alt="item.package_name" class="w-full h-full object-cover object-center" />
                            <div v-else class="tour-image-placeholder w-full h-full flex items-center justify-center flex-col p-3">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                                    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                                    <line x1="12" y1="22.08" x2="12" y2="12"></line>
                                </svg>
                                <p class="placeholder-text text-[15px] text-[#9CA3AF] uppercase mt-2">Image coming soon</p>
                            </div>
                            <!-- <div class="tour-type-badge">{{ item.type }}</div> -->
                        </div>

                        <div class="tour-card-content">
                            <div class="tour-card-header">
                                <h3 class="tour-title text-lg text-[var(--charcoal-dark)] font-semibold">{{ item.package_name }}</h3>
                                <div v-if="item.package_has_slots && item.duration" class="tour-duration text-base text-[var(--charcoal-dark)] flex items-center mb-1">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2" fill="none"/>
                                        <path d="M12 6V12L16 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                    </svg>
                                    <span class="ml-1">{{ item.duration }}</span>
                                </div>
                            </div>

                            <div class="tour-meta">
                                <div class="meta-badge text-base text-[var(--charcoal-dark)] flex items-center mb-1">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <rect x="3" y="6" width="18" height="15" rx="2" stroke="currentColor" stroke-width="2" fill="none"/>
                                        <path d="M3 10H21" stroke="currentColor" stroke-width="2"/>
                                    </svg>
                                    <span class="ml-1">
                                        {{ dateFormat(item.date) }}
                                        <span v-if="item.package_has_slots">@ {{ item.time_date }}</span>
                                    </span>
                                </div>
                            </div>

                            <p class="tour-description text-[15px] text-[var(--tone-gray)]">{{ item.long_description }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Continue Button -->
            <div class="continue-section mt-3">
                <button class="continue-button btn-primary" @click="continueToCheckout">
                    Continue to Checkout →
                </button>
            </div>
        </div>
        </section>
    </div>
</template>

<script>
    import Swal from 'sweetalert2';
    import NavBtns from './Nav/NavBtns.vue';
    import FlowNavigation from './Nav/FlowNavigation.vue';
    import { getMomentTimezone } from '../utils/dateUtils';

    export default {
        name: 'GoogleMap',
        title: 'Native American Tours',
        components: {
            NavBtns,
            FlowNavigation
        },
        data() {
            return {
                baseUrl: process.env.VUE_APP_BASE_URL,
                iframeStatus: true,
                tenantId: null,
                tourOperatorId: 0,
                comboIds: 0,
                cartItem: {},
                center: { lat: 0, lng: 0 },
                markers: [],
                mapOptions: {
                    zoomControl: true,
                    mapTypeControl: true,
                    scaleControl: true,
                    streetViewControl: true,
                    rotateControl: true,
                    fullscreenControl: true,
                    disableDefaultUi: false,
                    styles: [
                        {
                            featureType: 'water',
                            elementType: 'geometry',
                            stylers: [{ color: '#e9e9e9' }, { lightness: 17 }]
                        },
                        {
                            featureType: 'landscape',
                            elementType: 'geometry',
                            stylers: [{ color: '#f5f5f5' }, { lightness: 20 }]
                        },
                        {
                            featureType: 'road.highway',
                            elementType: 'geometry.fill',
                            stylers: [{ color: '#ffffff' }, { lightness: 17 }]
                        },
                        {
                            featureType: 'road.highway',
                            elementType: 'geometry.stroke',
                            stylers: [{ color: '#ffffff' }, { lightness: 29 }, { weight: 0.2 }]
                        },
                        {
                            featureType: 'road.arterial',
                            elementType: 'geometry',
                            stylers: [{ color: '#ffffff' }, { lightness: 18 }]
                        }
                    ]
                }
            };
        },
        computed: {
            tabs() {
                const hasCustomFields = this.$store.state.hasCustomFields;
                const isCombo = this.comboIds && this.comboIds !== 0;

                if (isCombo) {
                    // Combo WITH add-ons: Browse(1), Init(2), Addons(3), MyTrip(4), Maps(5), Checkout(6)
                    // Combo WITHOUT add-ons: Browse(1), Init(2), MyTrip(3), Maps(4), Checkout(5)
                    return hasCustomFields === false ? 4 : 5;
                }

                // Maps is only for combo packages, shouldn't be here for single packages
                return 1;
            },
            showComboWarning() {
                return this.comboIds && Object.keys(this.cartItem).length < 2;
            }
        },
        created() {
            this.initializeFromStore();
            this.buildMarkers();
            this.$store.dispatch('storeTabs', this.tabs);
            this.$store.dispatch('storeMindChange', 0);
        },
        methods: {
            initializeFromStore() {
                this.date = this.$store.state.date
                    ? this.$store.state.date
                    : getMomentTimezone(this.$store.state.timezone).format('YYYY-MM-DD');
                this.tenantId = this.$store.state.tenantId || null;
                this.tourOperatorId = this.$store.state.tourOperatorId || 0;
                this.iframeStatus = this.$store.state.iframeStatus;
                this.comboIds = this.$store.state.comboIds ?? 0;
                this.cartItem = this.$store.state.cartItem || {};
            },
            buildMarkers() {
                this.markers = [];
                this.center = { lat: 0, lng: 0 };
                let isFirstMarker = true;

                Object.values(this.cartItem).forEach(item => {
                    if (item.latitude && item.longitude) {
                        const lat = Number(item.latitude);
                        const lng = Number(item.longitude);
                        if (isFirstMarker) {
                            this.center = { lat, lng };
                            isFirstMarker = false;
                        }
                        this.markers.push({
                            position: { lat, lng },
                            title: item.package_name || 'Tour',
                            image: item.package_image || '',
                            duration: item.duration || '',
                            date: item.date || this.date,
                            time_date: item.time_date || '',
                            short_description: item.short_description || ''
                        });
                    }
                });
            },
            dateFormat(date) {
                return getMomentTimezone(this.$store.state.timezone, date).format('dddd, MMMM D, YYYY');
            },
            navigateToTab(tab, destination) {
                if ([1, 2, 3, 4, 5, 6].includes(tab)) {
                    if (tab === 2) {
                        this.$store.dispatch('storeFormData', null);
                    }
                    this.$store.dispatch('storeMindChange', 1);
                    this.$router.push({ name: destination });
                }
            },
            onMarkerClick(marker) {
                Swal.fire({
                    type: 'info',
                    toast: true,
                    timer: 5000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                    html: `
                    <div class="d-md-flex mt-2 items-start gap-3">
                        <div class="image-box" style="width: 100px; height: 100px; border-radius: 8px; overflow: hidden; background: #f9fafb; display: flex; align-items: center; justify-content: center;">
                        ${
                            marker.image
                            ? `<img src="${marker.image}" alt="${marker.title}" style="width: 100%; height: 100%; object-fit: cover;" />`
                            : `
                                <div class="tour-image-placeholder flex flex-col items-center justify-center text-center p-2" style="width: 100%; height: 100%;">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                                    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                                    <line x1="12" y1="22.08" x2="12" y2="12"></line>
                                </svg>
                                <p style="font-size: 13px; color: #9CA3AF; text-transform: uppercase; margin-top: 4px;">Image coming soon</p>
                                </div>
                            `
                        }
                        </div>
                        <div class="content-box" style="flex: 1; margin-top: 10px;">
                            <div><b>${marker.title}</b></div>
                            <p style="margin: 4px 0;">${this.dateFormat(marker.date)} @ ${marker.time_date || 'N/A'}</p>
                        </div>
                    </div>
                    `,
                });
            },
            continueToCheckout() {
                this.$router.push({ name: 'Checkout' });
            }
        }
    };
</script>

